/**
 * SettingsAchAccountsPage
 * ACH payment accounts settings tab.
 * Route: /settings/ach-accounts
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, CheckCircle2, Clock, AlertTriangle, Circle, RefreshCw } from 'lucide-react';
import {
  getStripeConnectStatus,
  listLandlordFundingSources,
  syncLandlordFundingSources,
  createSetupIntent,
  createLandlordFundingSource,
  setLandlordFundingSourcePrimary,
  removeLandlordFundingSource,
  updateLandlordFundingSourceName,
  verifyLandlordMicroDeposits,
  type StripeConnectStatus,
  type LandlordFundingSource,
  ApiClientError,
} from '../lib/api';
import { PageHeader } from '../components/layout/index';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { SettingsLayout } from '../components/settings/SettingsLayout';

export function SettingsAchAccountsPage(): JSX.Element {
  const [verificationStatus, setVerificationStatus] = useState<StripeConnectStatus | null>(null);
  const [fundingSources, setFundingSources] = useState<LandlordFundingSource[]>([]);
  const [isAchLoading, setIsAchLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [achError, setAchError] = useState<string | null>(null);
  const [sourceToRemove, setSourceToRemove] = useState<LandlordFundingSource | null>(null);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const loadAchData = useCallback(async (shouldSync = false) => {
    try {
      setIsAchLoading(true);
      setAchError(null);

      // When returning from Stripe Connect, sync funding sources first so the
      // bank account Stripe collected appears without requiring a manual refresh.
      if (shouldSync) {
        await syncLandlordFundingSources().catch(() => {
          // Best-effort — don't block the page load if sync fails
        });
      }

      const [verificationResponse, sourcesResponse] = await Promise.all([
        getStripeConnectStatus().catch((err) => {
          if (err instanceof ApiClientError && err.status === 404) return null;
          throw err;
        }),
        listLandlordFundingSources(),
      ]);
      setVerificationStatus(verificationResponse);
      setFundingSources(sourcesResponse.fundingSources);
    } catch (err) {
      setAchError('Failed to load payment information');
      console.error(err);
    } finally {
      setIsAchLoading(false);
    }
  }, []);

  useEffect(() => {
    const isStripeReturn = searchParams.get('stripeReturn') === 'true';
    if (isStripeReturn) {
      // Clean up the URL param so navigating back doesn't re-trigger sync
      setSearchParams({}, { replace: true });
    }
    loadAchData(isStripeReturn);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadAchData]);

  async function handleSync() {
    try {
      setIsSyncing(true);
      setAchError(null);
      const response = await syncLandlordFundingSources();
      setFundingSources(response.fundingSources);
    } catch (err) {
      setAchError(err instanceof ApiClientError ? err.message : 'Failed to refresh bank accounts');
      console.error(err);
    } finally {
      setIsSyncing(false);
    }
  }

  async function handleSetPrimary(id: string) {
    try {
      setAchError(null);
      await setLandlordFundingSourcePrimary(id);
      await loadAchData();
    } catch (err) {
      setAchError(err instanceof ApiClientError ? err.message : 'Failed to set primary funding source');
      console.error(err);
    }
  }

  async function handleRemove(source: LandlordFundingSource) {
    try {
      setAchError(null);
      await removeLandlordFundingSource(source.id);
      setSourceToRemove(null);
      await loadAchData();
    } catch (err) {
      setAchError(err instanceof ApiClientError ? err.message : 'Failed to remove funding source');
      console.error(err);
    }
  }

  async function handleNameUpdate(id: string, friendlyName: string | null) {
    try {
      await updateLandlordFundingSourceName(id, friendlyName);
      await loadAchData();
    } catch (err) {
      setAchError(err instanceof ApiClientError ? err.message : 'Failed to update account name');
      throw err;
    }
  }

  const isVerified = verificationStatus?.status === 'active';
  const canAddFundingSource = verificationStatus?.status === 'active';

  if (isAchLoading) {
    return (
      <SettingsLayout>
        <div className="px-[var(--page-padding-x)]">
          <div className="mb-4">
            <Skeleton className="h-7 w-48 mb-2" />
            <Skeleton className="h-4 w-72" />
          </div>
          <Skeleton className="h-20 rounded-lg mb-6" />
          <Skeleton className="h-16 rounded-lg" />
        </div>
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout>
      <div data-testid="settings-ach-accounts-page">
        <div className="px-[var(--page-padding-x)]">
          <PageHeader
            title="ACH Accounts"
            subtitle="Receive rent via bank transfer. Tenants initiate payments through the platform."
            className="mb-6"
          />

          {achError && (
            <div
              data-testid="ach-error-message"
              className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 mb-6"
            >
              <p className="text-sm text-destructive m-0">{achError}</p>
            </div>
          )}

          {/* Verification Status */}
          <section aria-labelledby="ach-verification-heading" className="mb-6">
            <h2 id="ach-verification-heading" className="text-sm font-semibold text-foreground mb-2">
              Verification Status
            </h2>
            <VerificationStatusCard
              status={verificationStatus}
              onManage={() => navigate('/verification')}
            />
          </section>

          {/* Bank Accounts */}
          <section aria-labelledby="ach-bank-accounts-heading" data-testid="ach-payment-section">
            <div className="flex items-center justify-between gap-4 mb-3">
              <h2 id="ach-bank-accounts-heading" className="text-sm font-semibold text-foreground m-0">
                Bank Accounts
              </h2>
              {isVerified && canAddFundingSource && (
                <AddBankAccountButton onSuccess={loadAchData} />
              )}
            </div>

            {!isVerified ? (
              <NotVerifiedMessage status={verificationStatus?.status} />
            ) : fundingSources.length > 0 ? (
              <FundingSourcesList
                sources={fundingSources}
                onSetPrimary={handleSetPrimary}
                onRemove={(source) => setSourceToRemove(source)}
                onNameUpdate={handleNameUpdate}
                onVerified={() => loadAchData()}
              />
            ) : (
              <AchEmptyState onSync={handleSync} isSyncing={isSyncing} />
            )}
          </section>
        </div>
      </div>

      {/* Remove Funding Source Modal */}
      {sourceToRemove && (
        <RemoveFundingSourceModal
          source={sourceToRemove}
          onConfirm={() => handleRemove(sourceToRemove)}
          onCancel={() => setSourceToRemove(null)}
        />
      )}
    </SettingsLayout>
  );
}

/* ── Verification Status Card ── */

interface VerificationStatusCardProps {
  status: StripeConnectStatus | null;
  onManage: () => void;
}

function VerificationStatusCard({ status, onManage }: VerificationStatusCardProps) {
  type StatusKey = 'active' | 'onboarding' | 'restricted' | 'not_started';

  const statusConfig: Record<StatusKey, {
    icon: React.ReactNode;
    title: string;
    message: string;
    border: string;
    bg: string;
  }> = {
    active: {
      icon: <CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" />,
      title: 'Verified',
      message: 'Your identity has been verified. You can receive rent payments.',
      border: 'border-success/30',
      bg: 'bg-success/5',
    },
    onboarding: {
      icon: <Clock className="h-4 w-4 text-warning" aria-hidden="true" />,
      title: 'Verification in progress',
      message: 'Your verification is not yet complete. Continue where you left off.',
      border: 'border-warning/30',
      bg: 'bg-warning/5',
    },
    restricted: {
      icon: <AlertTriangle className="h-4 w-4 text-destructive" aria-hidden="true" />,
      title: 'Action required',
      message: 'Additional information is needed to complete your verification.',
      border: 'border-destructive/30',
      bg: 'bg-destructive/5',
    },
    not_started: {
      icon: <Circle className="h-4 w-4 text-muted-foreground" aria-hidden="true" />,
      title: 'Not started',
      message: 'Complete verification to receive rent payments.',
      border: 'border-border',
      bg: 'bg-card',
    },
  };

  const key: StatusKey =
    status?.status && status.status in statusConfig
      ? (status.status as StatusKey)
      : 'not_started';
  const config = statusConfig[key];

  return (
    <div
      data-testid="verification-status-card"
      className={`rounded-lg border ${config.border} ${config.bg} p-4 flex items-center justify-between gap-4`}
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <span className="shrink-0 mt-0.5">{config.icon}</span>
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground m-0">{config.title}</p>
          <p className="text-sm text-muted-foreground m-0 mt-0.5">{config.message}</p>
        </div>
      </div>
      <Button
        onClick={onManage}
        variant="outline"
        size="sm"
        data-testid="verification-manage-button"
        className="min-h-[44px] shrink-0"
      >
        Manage
      </Button>
    </div>
  );
}

/* ── Not Verified Message ── */

interface NotVerifiedMessageProps {
  status?: string;
}

function NotVerifiedMessage({ status }: NotVerifiedMessageProps) {
  const getMessage = () => {
    switch (status) {
      case 'onboarding':
        return 'Your verification is in progress. You can add bank accounts once verification is complete.';
      case 'restricted':
        return 'Your account requires additional information. Please complete verification to add bank accounts.';
      default:
        return 'Complete identity verification before adding bank accounts.';
    }
  };

  return (
    <div
      data-testid="not-verified-message"
      className="rounded-lg border border-border bg-card p-4 flex items-start gap-3"
    >
      <AlertTriangle className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
      <p className="text-sm text-muted-foreground m-0">{getMessage()}</p>
    </div>
  );
}

/* ── ACH Empty State ── */

interface AchEmptyStateProps {
  onSync: () => void;
  isSyncing: boolean;
}

function AchEmptyState({ onSync, isSyncing }: AchEmptyStateProps) {
  return (
    <div
      data-testid="ach-empty-state"
      className="rounded-lg border border-border bg-card p-4 flex items-center justify-between gap-4"
    >
      <div className="flex items-start gap-3 min-w-0">
        <Building2 className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-sm text-muted-foreground m-0">
          No bank accounts connected yet. If you added one during verification, it may take a
          moment to appear.
        </p>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={onSync}
        disabled={isSyncing}
        data-testid="sync-funding-sources-button"
        className="min-h-[44px] shrink-0 gap-1.5"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${isSyncing ? 'animate-spin' : ''}`} aria-hidden="true" />
        {isSyncing ? 'Syncing...' : 'Sync'}
      </Button>
    </div>
  );
}

/* ── Funding Sources List ── */

interface FundingSourcesListProps {
  sources: LandlordFundingSource[];
  onSetPrimary: (id: string) => void;
  onRemove: (source: LandlordFundingSource) => void;
  onNameUpdate: (id: string, friendlyName: string | null) => Promise<void>;
  onVerified: () => void;
}

function FundingSourcesList({ sources, onSetPrimary, onRemove, onNameUpdate, onVerified }: FundingSourcesListProps) {
  return (
    <div data-testid="funding-sources-list" className="flex flex-col gap-3">
      {sources.map((source) => (
        <FundingSourceCard
          key={source.id}
          source={source}
          onSetPrimary={onSetPrimary}
          onRemove={onRemove}
          onNameUpdate={onNameUpdate}
          onVerified={onVerified}
        />
      ))}
    </div>
  );
}

/* ── Funding Source Card ── */

interface FundingSourceCardProps {
  source: LandlordFundingSource;
  onSetPrimary: (id: string) => void;
  onRemove: (source: LandlordFundingSource) => void;
  onNameUpdate: (id: string, friendlyName: string | null) => Promise<void>;
  onVerified: () => void;
}

/**
 * Pending-verification banner copy, keyed off the method Stripe actually reported.
 * A method is named only when we have one; an unknown (null) method gets
 * method-neutral copy that doesn't claim a specific verification path.
 */
function pendingVerificationMessage(verificationMethod: LandlordFundingSource['verificationMethod']): string {
  switch (verificationMethod) {
    case 'instant_micro_deposit':
      return 'We sent a $0.01 deposit to this bank account. On the statement, its description ends in a 6-character code (like SM11AA). Enter that code below.';
    case 'micro_deposit':
      return 'We sent two small deposits to this bank account — they usually arrive within 1–2 business days. Once you see them, enter both amounts below.';
    default:
      return "We're verifying this bank account. Within 1–2 business days you'll see either one $0.01 deposit with a 6-character code in its description, or two small deposits. Use the matching option below for whichever you see.";
  }
}

function FundingSourceCard({ source, onSetPrimary, onRemove, onNameUpdate, onVerified }: FundingSourceCardProps) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(source.friendlyName || '');
  const [isSavingName, setIsSavingName] = useState(false);

  const statusBadgeClass = {
    verified: 'bg-success/10 text-success border-success/20',
    unverified: 'bg-warning/10 text-warning border-warning/20',
    pending: 'bg-warning/10 text-warning border-warning/20',
    failed: 'bg-destructive/10 text-destructive border-destructive/20',
  }[source.status as 'verified' | 'unverified' | 'pending' | 'failed'] ?? 'bg-warning/10 text-warning border-warning/20';

  // Humanized label (contrast with the raw enum value): matches the tenant page's
  // equivalent badgeLabel mapping.
  const statusBadgeLabel = {
    verified: 'Verified',
    unverified: 'Unverified',
    pending: 'Pending Verification',
    failed: 'Failed',
  }[source.status as 'verified' | 'unverified' | 'pending' | 'failed'] ?? source.status;

  const cardBorder = source.isPrimary ? 'border-2 border-primary' : 'border border-border';
  const displayName = source.friendlyName || source.bankName;

  async function handleSaveName() {
    setIsSavingName(true);
    try {
      await onNameUpdate(source.id, nameValue.trim() || null);
      setIsEditingName(false);
    } catch {
      // Parent handles error display
    } finally {
      setIsSavingName(false);
    }
  }

  return (
    <div
      data-testid={`funding-source-card-${source.id}`}
      className={`rounded-lg bg-card p-4 flex flex-col gap-3 ${cardBorder}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Building2 className="h-5 w-5 text-muted-foreground shrink-0" aria-hidden="true" />
          <div className="flex-1 min-w-0">
            {isEditingName ? (
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  maxLength={100}
                  placeholder={source.bankName}
                  className="text-sm border border-input rounded px-2 py-1 min-w-0 flex-1 bg-background text-foreground"
                  data-testid={`funding-source-name-input-${source.id}`}
                  aria-label="Account friendly name"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveName();
                    if (e.key === 'Escape') {
                      setIsEditingName(false);
                      setNameValue(source.friendlyName || '');
                    }
                  }}
                />
                <Button
                  size="sm"
                  onClick={handleSaveName}
                  disabled={isSavingName}
                  className="min-h-[44px]"
                  data-testid={`funding-source-name-save-${source.id}`}
                  aria-label="Save account name"
                >
                  {isSavingName ? '...' : 'Save'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setIsEditingName(false);
                    setNameValue(source.friendlyName || '');
                  }}
                  className="min-h-[44px]"
                  aria-label="Cancel editing account name"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-foreground">{displayName}</span>
                <button
                  onClick={() => setIsEditingName(true)}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="Edit account name"
                  data-testid={`funding-source-name-edit-${source.id}`}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
                {source.isPrimary && (
                  <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 text-xs">
                    Primary
                  </Badge>
                )}
              </div>
            )}
            <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1 ph-no-capture">
              {source.accountType} •••• {source.accountMask}
              <span className="mx-0.5">·</span>
              <Badge variant="secondary" className={`border text-xs ${statusBadgeClass}`}>
                {statusBadgeLabel}
              </Badge>
            </div>
          </div>
        </div>

        <div className="flex gap-2 shrink-0 flex-wrap">
          {!source.isPrimary && source.status === 'verified' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSetPrimary(source.id)}
              data-testid={`set-primary-button-${source.id}`}
              className="min-h-[44px]"
            >
              Set as Primary
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRemove(source)}
            data-testid={`remove-funding-source-button-${source.id}`}
            className="min-h-[44px] text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            Remove
          </Button>
        </div>
      </div>

      {(source.status === 'pending' || source.status === 'failed') && (
        <div className="border-t border-border pt-4" data-testid="pending-verification-banner">
          <p className={`text-sm mb-3 ${source.status === 'failed' ? 'text-destructive' : 'text-muted-foreground'}`}>
            {source.status === 'failed'
              ? 'Your previous verification attempt was incorrect. Please try again.'
              : pendingVerificationMessage(source.verificationMethod)}
          </p>
          <LandlordMicroDepositVerificationForm
            fundingSourceId={source.id}
            verificationMethod={source.verificationMethod}
            onSuccess={onVerified}
          />
        </div>
      )}
    </div>
  );
}

/* ── Landlord Micro-Deposit Verification Form ── */

interface LandlordMicroDepositVerificationFormProps {
  fundingSourceId: string;
  verificationMethod: 'micro_deposit' | 'instant_micro_deposit' | 'instant' | null;
  onSuccess: () => void;
}

function LandlordMicroDepositVerificationForm({
  fundingSourceId,
  verificationMethod,
  onSuccess,
}: LandlordMicroDepositVerificationFormProps) {
  // Default to descriptor-code mode when the method is unknown (null): Stripe documents
  // descriptor_code as the default microdeposit verification type, so it's the best initial
  // guess absent a reported method. A known non-descriptor method still opens in amounts mode.
  const isDescriptorMode = verificationMethod === 'instant_micro_deposit' || verificationMethod === null;
  const [useDescriptorCode, setUseDescriptorCode] = useState(isDescriptorMode);
  const [amount1, setAmount1] = useState('');
  const [amount2, setAmount2] = useState('');
  const [descriptorCode, setDescriptorCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      if (useDescriptorCode) {
        if (!descriptorCode.trim()) {
          setError('Enter the 6-character code from your statement.');
          setIsSubmitting(false);
          return;
        }
        await verifyLandlordMicroDeposits(fundingSourceId, {
          type: 'instant_micro_deposit',
          descriptorCode: descriptorCode.trim(),
        });
      } else {
        const cents1 = Math.round(parseFloat(amount1) * 100);
        const cents2 = Math.round(parseFloat(amount2) * 100);
        if (isNaN(cents1) || isNaN(cents2) || cents1 <= 0 || cents2 <= 0) {
          setError('Enter both deposit amounts in dollars (for example, 0.32).');
          setIsSubmitting(false);
          return;
        }
        await verifyLandlordMicroDeposits(fundingSourceId, {
          type: 'micro_deposit',
          amounts: [cents1, cents2],
        });
      }

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed. Please check your values and try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="landlord-micro-deposit-verification-form"
    >
      {error && (
        <div
          id={`landlord-micro-deposit-error-${fundingSourceId}`}
          className="bg-destructive/5 border border-destructive/20 rounded-lg p-3 mb-4"
          data-testid="landlord-micro-deposit-error"
          role="alert"
        >
          <p className="text-sm text-destructive m-0">{error}</p>
        </div>
      )}

      {useDescriptorCode ? (
        <div className="mb-4">
          <label
            htmlFor={`landlord-descriptor-${fundingSourceId}`}
            className="text-sm font-medium text-foreground block mb-1.5"
          >
            6-character code from your statement
          </label>
          <p className="text-xs text-muted-foreground mb-2">
            Find the $0.01 deposit on your statement. Its description ends with a 6-character code — enter it here.
          </p>
          <input
            id={`landlord-descriptor-${fundingSourceId}`}
            type="text"
            value={descriptorCode}
            onChange={(e) => setDescriptorCode(e.target.value.toUpperCase())}
            placeholder="XXXXXX"
            maxLength={6}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="landlord-micro-deposit-descriptor-code"
            aria-label="6-character code from your bank statement"
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `landlord-micro-deposit-error-${fundingSourceId}` : undefined}
          />
        </div>
      ) : (
        <>
          <p className="text-xs text-muted-foreground mb-3">
            Two small deposits will appear in your bank account within 1–2 business days. Enter both amounts below.
          </p>
          <div className="flex gap-3 mb-4">
            <div className="flex-1">
              <label
                htmlFor={`landlord-amount1-${fundingSourceId}`}
                className="text-sm font-medium text-foreground block mb-1.5"
              >
                First deposit ($)
              </label>
              <input
                id={`landlord-amount1-${fundingSourceId}`}
                type="number"
                min="0.01"
                step="0.01"
                value={amount1}
                onChange={(e) => setAmount1(e.target.value)}
                placeholder="0.00"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="landlord-micro-deposit-amount-1"
                aria-label="First micro-deposit amount in dollars"
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? `landlord-micro-deposit-error-${fundingSourceId}` : undefined}
              />
            </div>
            <div className="flex-1">
              <label
                htmlFor={`landlord-amount2-${fundingSourceId}`}
                className="text-sm font-medium text-foreground block mb-1.5"
              >
                Second deposit ($)
              </label>
              <input
                id={`landlord-amount2-${fundingSourceId}`}
                type="number"
                min="0.01"
                step="0.01"
                value={amount2}
                onChange={(e) => setAmount2(e.target.value)}
                placeholder="0.00"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="landlord-micro-deposit-amount-2"
                aria-label="Second micro-deposit amount in dollars"
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? `landlord-micro-deposit-error-${fundingSourceId}` : undefined}
              />
            </div>
          </div>
        </>
      )}

      <div className="flex flex-col gap-2">
        <Button
          type="submit"
          disabled={isSubmitting}
          data-testid="landlord-micro-deposit-submit-button"
          className="min-h-[44px]"
        >
          {isSubmitting ? 'Verifying...' : 'Verify Bank Account'}
        </Button>

        {/* Always rendered: with no repair mechanism for a stale or unknown verification
            method, this toggle is the only way for a user whose reality differs from the
            stored value to reach the other input mode (BR-8b). */}
        <button
          type="button"
          onClick={() => setUseDescriptorCode((prev) => !prev)}
          className="text-xs text-muted-foreground hover:text-foreground underline text-center"
          data-testid="landlord-micro-deposit-toggle-mode"
        >
          {useDescriptorCode ? 'I see two small deposits instead' : 'I see one deposit with a code instead'}
        </button>
      </div>
    </form>
  );
}

/* ── Add Bank Account Button ── */

interface AddBankAccountButtonProps {
  onSuccess: () => void;
}

function AddBankAccountButton({ onSuccess }: AddBankAccountButtonProps) {
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleAddBankAccount() {
    setIsConnecting(true);
    setError(null);

    try {
      const { clientSecret } = await createSetupIntent();

      const { loadStripe } = await import('@stripe/stripe-js');
      const stripe = await loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);
      if (!stripe) throw new Error('Payment system unavailable. Please try again.');

      const collectResult = await stripe.collectBankAccountForSetup({
        clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: {
            billing_details: { name: 'Account Holder' },
          },
        },
      });

      if (collectResult.error) throw new Error(collectResult.error.message);
      if (!collectResult.setupIntent) return;

      if (collectResult.setupIntent.status === 'requires_confirmation') {
        const confirmResult = await stripe.confirmUsBankAccountSetup(clientSecret);
        if (confirmResult.error) throw new Error(confirmResult.error.message);
        if (!confirmResult.setupIntent) throw new Error('No setup intent returned. Please try again.');

        const si = confirmResult.setupIntent;
        const pmId =
          typeof si.payment_method === 'string'
            ? si.payment_method
            : si.payment_method?.id;

        if (!pmId) throw new Error('No payment method returned. Please try again.');

        if (si.status === 'requires_action') {
          await createLandlordFundingSource({ paymentMethodId: pmId, setupIntentId: si.id });
          onSuccess();
          return;
        }

        await createLandlordFundingSource({ paymentMethodId: pmId, setupIntentId: si.id });
      } else if (collectResult.setupIntent.status === 'succeeded') {
        const pmId =
          typeof collectResult.setupIntent.payment_method === 'string'
            ? collectResult.setupIntent.payment_method
            : collectResult.setupIntent.payment_method?.id;

        if (!pmId) throw new Error('No payment method returned. Please try again.');
        await createLandlordFundingSource({ paymentMethodId: pmId, setupIntentId: collectResult.setupIntent.id });
      } else {
        throw new Error('Bank account connection was not completed. Please try again.');
      }

      onSuccess();
    } catch (err) {
      setError(
        err instanceof ApiClientError || err instanceof Error
          ? err.message
          : 'Failed to connect bank account'
      );
      console.error(err);
    } finally {
      setIsConnecting(false);
    }
  }

  return (
    <div>
      {error && <p className="text-xs text-destructive mb-1">{error}</p>}
      <Button
        size="sm"
        onClick={handleAddBankAccount}
        disabled={isConnecting}
        data-testid="add-bank-account-button"
        className="min-h-[44px]"
      >
        {isConnecting ? 'Connecting...' : '+ Add Bank Account'}
      </Button>
    </div>
  );
}

/* ── Remove Funding Source Modal ── */

interface RemoveFundingSourceModalProps {
  source: LandlordFundingSource;
  onConfirm: () => void;
  onCancel: () => void;
}

function RemoveFundingSourceModal({ source, onConfirm, onCancel }: RemoveFundingSourceModalProps) {
  return (
    <div
      data-testid="remove-funding-source-modal"
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="remove-modal-title"
    >
      <div
        className="rounded-lg border border-border bg-card shadow-lg max-w-sm w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="remove-modal-title" className="text-base font-semibold text-foreground m-0">
          Remove bank account?
        </h3>
        <p className="text-sm text-muted-foreground mt-2 m-0">
          Remove <span className="font-medium text-foreground">{source.bankName}</span> ending in{' '}
          {source.accountMask}?
        </p>
        {source.isPrimary && (
          <div className="rounded-lg border border-warning/30 bg-warning/5 p-3 mt-3">
            <p className="text-sm text-warning m-0">
              This is your primary account. You&apos;ll need to set another as primary after removing it.
            </p>
          </div>
        )}
        <div className="flex gap-2 mt-5">
          <Button
            variant="outline"
            onClick={onCancel}
            data-testid="remove-modal-cancel"
            className="flex-1 min-h-[44px]"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            data-testid="remove-modal-confirm"
            className="flex-1 min-h-[44px]"
          >
            Remove
          </Button>
        </div>
      </div>
    </div>
  );
}

export default SettingsAchAccountsPage;
